import { getAppState } from '../../app';
import {
  sedrApi,
  type SedrCategory,
  type SedrRequirementLinkType,
  type SedrRoomDetail,
} from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { showConfirmDialog } from '../../ui/confirmDialog';
import { showInputDialog } from '../../ui/inputDialog';
import { showSedrEditLinkTypeDialog } from './sedrEditLinkTypeDialog';
import { showSedrRoomCategoryDialog } from './sedrRoomCategoryDialog';
import { sedrErrorMessageKey } from './sedrErrors';
import { invalidateSedrCategoriesCache, tSedrLinkTypeName } from './sedrUi';

const DELETION_GRACE_DAYS = 7;

function schedulePurgePreview(): string {
  return new Date(Date.now() + DELETION_GRACE_DAYS * 24 * 60 * 60 * 1000).toLocaleString();
}

export interface SedrRoomSettingsPanelOptions {
  room: SedrRoomDetail;
  canManage: boolean;
  onRoomChanged: () => void | Promise<void>;
  onDeleted: () => void;
}

function lifecycleBadgeKey(room: SedrRoomDetail): string {
  if (room.archivedAt) return 'sedr.archive.status';
  const state = room.lifecycleState;
  if (state === 'SetupIncomplete') return 'sedr.lifecycle.SetupIncomplete';
  if (state === 'DeletionRequested') return 'sedr.lifecycle.DeletionRequested';
  if (state === 'PendingDeletion') return 'sedr.lifecycle.PendingDeletion';
  return 'sedr.lifecycle.Active';
}

function lifecycleBadgeTone(room: SedrRoomDetail): string {
  if (room.archivedAt) return 'status-badge--muted';
  if (room.lifecycleState === 'PendingDeletion' || room.lifecycleState === 'DeletionRequested') {
    return 'status-badge--danger';
  }
  if (room.lifecycleState === 'SetupIncomplete') return 'status-badge--warning';
  return 'status-badge--success';
}

export async function renderSedrRoomSettingsPanel(
  host: HTMLElement,
  options: SedrRoomSettingsPanelOptions,
): Promise<void> {
  const { room, canManage, onRoomChanged, onDeleted } = options;
  if (!canManage) {
    host.innerHTML = `<p class="error-text" data-i18n="status.error"></p>`;
    applyI18n(host);
    return;
  }

  const roomId = room.id;
  const roomType = room.roomType ?? 'ExternalAudit';
  const guestLinksForced = roomType === 'ExternalAudit';
  // Identity / category / link-type edits blocked when archived or pending deletion;
  // lifecycle actions (unarchive / cancel deletion) remain available.
  const canEditSettings = !(Boolean(room.archivedAt) || room.lifecycleState === 'PendingDeletion');
  let requireGuestTotp = Boolean(room.requireGuestTotp);
  let allowGuestLinks = room.allowGuestLinks !== false;
  let categories: SedrCategory[] = [];
  let linkTypes: SedrRequirementLinkType[] = [];
  let busy = false;

  try {
    [categories, linkTypes] = await Promise.all([
      sedrApi.listCategories(roomId),
      sedrApi.listRequirementLinkTypes(roomId),
    ]);
  } catch (cause) {
    console.error('Load room settings failed', cause);
    host.innerHTML = `<p class="error-text" data-i18n="status.error"></p>`;
    applyI18n(host);
    return;
  }

  const pinLifecycleTop =
    !room.archivedAt &&
    (room.lifecycleState === 'DeletionRequested' || room.lifecycleState === 'PendingDeletion');

  const lifecycleCardHtml = `
    <section class="sedr-settings-card sedr-settings-card--danger">
      <header class="sedr-settings-card__header">
        <div class="sedr-settings-card__title-row">
          <h3 data-i18n="sedr.settings.lifecycleTitle"></h3>
          <span class="status-badge ${lifecycleBadgeTone(room)}" data-i18n="${lifecycleBadgeKey(room)}"></span>
        </div>
        <p class="muted" data-i18n="sedr.settings.lifecycleHint"></p>
      </header>
      <div class="sedr-settings-lifecycle-actions" id="sedr-settings-lifecycle-actions"></div>
    </section>
  `;

  const panel = document.createElement('div');
  panel.className = 'sedr-tab-panel sedr-settings-panel';
  panel.innerHTML = `
    <div class="sedr-settings-flash" id="sedr-settings-flash" hidden>
      <p class="error-text" id="sedr-settings-error" hidden></p>
      <p class="sedr-settings-flash__info" id="sedr-settings-info" hidden></p>
    </div>

    ${!canEditSettings ? `
      <div class="sedr-settings-banner" role="status">
        <p data-i18n="${room.archivedAt ? 'sedr.archive.readOnlyHint' : 'sedr.error.roomPendingDeletion'}"></p>
      </div>
    ` : ''}

    ${pinLifecycleTop ? lifecycleCardHtml : ''}

    <section class="sedr-settings-card">
      <header class="sedr-settings-card__header">
        <h3 data-i18n="sedr.settings.identityTitle"></h3>
        <p class="muted" data-i18n="sedr.settings.identityHint"></p>
      </header>
      <form class="sedr-settings-identity-form" id="sedr-settings-identity-form">
        <div class="sedr-settings-identity-fields">
          <label>
            <span data-i18n="sedr.settings.roomTitle"></span>
            <input type="text" name="title" maxlength="200" required autocomplete="off" ${!canEditSettings ? 'disabled' : ''} />
          </label>
          <label>
            <span data-i18n="sedr.settings.clientOrganization"></span>
            <input type="text" name="clientOrganization" maxlength="200" required autocomplete="off" ${!canEditSettings ? 'disabled' : ''} />
          </label>
        </div>
        ${!canEditSettings ? '' : `
          <div class="sedr-settings-card__actions">
            <button type="submit" class="button primary" data-i18n="sedr.settings.saveIdentity"></button>
          </div>
        `}
      </form>
    </section>

    <section class="sedr-settings-card">
      <header class="sedr-settings-card__header">
        <div class="sedr-settings-card__title-row">
          <h3 data-i18n="sedr.settings.guestTitle"></h3>
          <span class="status-badge status-badge--muted" data-i18n="sedr.roomType.${roomType}"></span>
        </div>
        <p class="muted" data-i18n="sedr.settings.guestHint"></p>
      </header>
      <div class="sedr-settings-prefs">
        <label class="sedr-settings-pref">
          <span class="sedr-settings-pref__text">
            <span class="sedr-settings-pref__title" data-i18n="sedr.access.allowGuestLinks"></span>
            <span class="muted" data-i18n="${guestLinksForced ? 'sedr.access.allowGuestLinksForcedHint' : 'sedr.access.allowGuestLinksHint'}"></span>
          </span>
          <input type="checkbox" id="sedr-settings-allow-guest-links" ${allowGuestLinks ? 'checked' : ''} ${guestLinksForced || !canEditSettings ? 'disabled' : ''} />
        </label>
        <label class="sedr-settings-pref">
          <span class="sedr-settings-pref__text">
            <span class="sedr-settings-pref__title" data-i18n="sedr.access.requireGuestTotp"></span>
            <span class="muted" data-i18n="sedr.access.requireGuestTotpHint"></span>
          </span>
          <input type="checkbox" id="sedr-settings-require-guest-totp" ${requireGuestTotp ? 'checked' : ''} ${!canEditSettings ? 'disabled' : ''} />
        </label>
      </div>
    </section>

    <section class="sedr-settings-card">
      <header class="sedr-settings-card__header sedr-settings-card__header--split">
        <div>
          <h3 data-i18n="sedr.settings.categoriesTitle"></h3>
          <p class="muted" data-i18n="sedr.settings.categoriesHint"></p>
        </div>
        ${!canEditSettings ? '' : `<button type="button" class="button primary" id="sedr-settings-create-category" data-i18n="sedr.settings.createCategory"></button>`}
      </header>
      <ul class="sedr-settings-list" id="sedr-settings-categories-list"></ul>
      <p class="muted sedr-settings-empty" id="sedr-settings-categories-empty" hidden data-i18n="sedr.settings.categoriesEmpty"></p>
    </section>

    <section class="sedr-settings-card">
      <header class="sedr-settings-card__header sedr-settings-card__header--split">
        <div>
          <h3 data-i18n="sedr.links.manageTypesTitle"></h3>
          <p class="muted" data-i18n="sedr.links.manageTypesHint"></p>
        </div>
        ${!canEditSettings ? '' : `<button type="button" class="button subtle" id="sedr-settings-add-link-type" data-i18n="sedr.links.addCustomType"></button>`}
      </header>
      <ul class="sedr-settings-list sedr-settings-list--types" id="sedr-settings-link-types"></ul>
    </section>

    ${pinLifecycleTop ? '' : lifecycleCardHtml}
  `;

  host.replaceChildren(panel);
  applyI18n(panel);

  const flashEl = panel.querySelector('#sedr-settings-flash') as HTMLElement;
  const errorEl = panel.querySelector('#sedr-settings-error') as HTMLParagraphElement;
  const infoEl = panel.querySelector('#sedr-settings-info') as HTMLParagraphElement;
  const identityForm = panel.querySelector('#sedr-settings-identity-form') as HTMLFormElement;
  const titleInput = identityForm.querySelector('input[name="title"]') as HTMLInputElement;
  const orgInput = identityForm.querySelector('input[name="clientOrganization"]') as HTMLInputElement;
  titleInput.value = room.title;
  orgInput.value = room.clientOrganization;

  const clearMessages = () => {
    flashEl.hidden = true;
    errorEl.hidden = true;
    infoEl.hidden = true;
  };
  const showError = (message: string) => {
    infoEl.hidden = true;
    flashEl.hidden = false;
    errorEl.hidden = false;
    errorEl.textContent = message;
    flashEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };
  const showInfo = (message: string) => {
    errorEl.hidden = true;
    flashEl.hidden = false;
    infoEl.hidden = false;
    infoEl.textContent = message;
    flashEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const me = (() => {
    const state = getAppState();
    return state.kind === 'authenticated' ? state.session.userId : '';
  })();

  const renderCategories = () => {
    const listEl = panel.querySelector('#sedr-settings-categories-list') as HTMLUListElement;
    const emptyEl = panel.querySelector('#sedr-settings-categories-empty') as HTMLParagraphElement;
    listEl.replaceChildren();
    emptyEl.hidden = categories.length > 0;

    for (const category of categories) {
      const li = document.createElement('li');
      li.className = 'sedr-settings-list__item';
      const label = document.createElement('span');
      label.className = 'sedr-settings-list__label';
      label.textContent = category.name;
      li.append(label);

      if (canEditSettings) {
        const actions = document.createElement('div');
        actions.className = 'sedr-settings-list__actions';
        const renameBtn = document.createElement('button');
        renameBtn.type = 'button';
        renameBtn.className = 'button ghost';
        renameBtn.setAttribute('data-i18n', 'sedr.settings.renameCategory');
        renameBtn.addEventListener('click', () => {
          showSedrRoomCategoryDialog({
            roomId,
            mode: 'rename',
            categoryId: category.id,
            initialName: category.name,
            onDone: async () => {
              categories = await sedrApi.listCategories(roomId);
              renderCategories();
            },
          });
        });
        const deleteBtn = document.createElement('button');
        deleteBtn.type = 'button';
        deleteBtn.className = 'button danger';
        deleteBtn.setAttribute('data-i18n', 'common.delete');
        deleteBtn.addEventListener('click', async () => {
          const confirmed = await showConfirmDialog({
            titleKey: 'sedr.settings.deleteCategoryTitle',
            messageKey: 'sedr.settings.deleteCategoryMessage',
            messageParams: { name: category.name },
            confirmLabelKey: 'common.delete',
          });
          if (!confirmed) return;
          try {
            await sedrApi.deleteCategory(roomId, category.id);
            invalidateSedrCategoriesCache(roomId);
            categories = await sedrApi.listCategories(roomId);
            renderCategories();
            showInfo(t('sedr.settings.categoryDeleted'));
          } catch (cause) {
            showError(t(sedrErrorMessageKey(cause)));
          }
        });
        actions.append(renameBtn, deleteBtn);
        li.append(actions);
      }
      listEl.append(li);
    }
    applyI18n(listEl);
  };

  const renderLinkTypes = () => {
    const listEl = panel.querySelector('#sedr-settings-link-types') as HTMLUListElement;
    listEl.replaceChildren();
    const sorted = [...linkTypes].sort((a, b) => {
      if (a.isBuiltIn && !b.isBuiltIn) return -1;
      if (!a.isBuiltIn && b.isBuiltIn) return 1;
      return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
    });
    for (const type of sorted) {
      const li = document.createElement('li');
      li.className = `sedr-settings-list__item${type.isBuiltIn ? ' sedr-settings-list__item--builtin' : ''}`;
      const name = type.isBuiltIn
        ? tSedrLinkTypeName(type.id, type.name, false)
        : type.name;
      const inverse = type.isBuiltIn
        ? tSedrLinkTypeName(type.id, type.inverseName ?? type.name, true)
        : (type.inverseName || type.name);
      const main = document.createElement('div');
      main.className = 'sedr-settings-list__main';
      const title = document.createElement('span');
      title.className = 'sedr-settings-list__label';
      title.textContent = name;
      main.append(title);
      if (inverse && inverse !== name) {
        const inv = document.createElement('span');
        inv.className = 'muted sedr-settings-list__meta';
        inv.textContent = `↔ ${inverse}`;
        main.append(inv);
      }
      if (type.isBuiltIn) {
        const badge = document.createElement('span');
        badge.className = 'status-badge status-badge--muted';
        badge.textContent = t('sedr.links.builtIn');
        main.append(badge);
      }
      li.append(main);
      if (canEditSettings && !type.isBuiltIn) {
        const actions = document.createElement('div');
        actions.className = 'sedr-settings-list__actions';
        const editBtn = document.createElement('button');
        editBtn.type = 'button';
        editBtn.className = 'button ghost';
        editBtn.textContent = t('sedr.links.editType');
        editBtn.addEventListener('click', () => {
          void (async () => {
            const result = await showSedrEditLinkTypeDialog({
              mode: 'edit',
              initialName: type.name,
              initialInverseName: type.inverseName,
            });
            if (!result) return;
            try {
              const updated = await sedrApi.patchRequirementLinkType(roomId, type.id, {
                name: result.name,
                inverseName: result.inverseName,
                clearInverseName: !result.inverseName,
              });
              linkTypes = linkTypes.map((x) => (x.id === type.id ? updated : x));
              renderLinkTypes();
            } catch (cause) {
              showError(t(sedrErrorMessageKey(cause)));
            }
          })();
        });
        actions.append(editBtn);
        li.append(actions);
      }
      listEl.append(li);
    }
  };

  const renderLifecycle = () => {
    const wrap = panel.querySelector('#sedr-settings-lifecycle-actions') as HTMLElement;
    wrap.replaceChildren();
    const appendBtn = (labelKey: string, className: string, onClick: () => void) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = className;
      btn.setAttribute('data-i18n', labelKey);
      btn.addEventListener('click', onClick);
      wrap.append(btn);
    };

    if (room.archivedAt) {
      appendBtn('sedr.archive.unarchiveAction', 'button primary', () => {
        void (async () => {
          const confirmed = await showConfirmDialog({
            titleKey: 'sedr.archive.unarchiveTitle',
            messageKey: 'sedr.archive.unarchiveMessage',
            messageParams: { title: room.title },
            confirmLabelKey: 'sedr.archive.unarchiveAction',
            confirmClassName: 'primary',
          });
          if (!confirmed) return;
          try {
            await sedrApi.unarchiveRoom(roomId);
            await onRoomChanged();
          } catch (cause) {
            showError(t(sedrErrorMessageKey(cause)));
          }
        })();
      });
    } else if (room.lifecycleState === 'SetupIncomplete') {
      appendBtn('sedr.deletion.deleteNow', 'button danger', () => {
        void (async () => {
          const confirmed = await showConfirmDialog({
            titleKey: 'sedr.deletion.deleteNowTitle',
            messageKey: 'sedr.deletion.confirmDeleteNow',
            messageParams: { title: room.title },
            confirmLabelKey: 'sedr.deletion.deleteNow',
          });
          if (!confirmed) return;
          try {
            await sedrApi.deleteRoom(roomId);
            onDeleted();
          } catch (cause) {
            showError(t(sedrErrorMessageKey(cause)));
          }
        })();
      });
    } else if (room.lifecycleState === 'Active') {
      appendBtn('sedr.archive.action', 'button subtle', () => {
        void (async () => {
          const confirmed = await showConfirmDialog({
            titleKey: 'sedr.archive.confirmTitle',
            messageKey: 'sedr.archive.confirmMessage',
            messageParams: { title: room.title },
            confirmLabelKey: 'sedr.archive.action',
            confirmClassName: 'primary',
          });
          if (!confirmed) return;
          try {
            await sedrApi.archiveRoom(roomId);
            await onRoomChanged();
          } catch (cause) {
            showError(t(sedrErrorMessageKey(cause)));
          }
        })();
      });
      appendBtn('sedr.deletion.request', 'button danger', () => {
        void (async () => {
          const confirmed = await showConfirmDialog({
            titleKey: 'sedr.deletion.requestTitle',
            messageKey: 'sedr.deletion.confirmRequestDetail',
            messageParams: {
              title: room.title,
              client: room.clientOrganization,
              files: room.fileCount,
              requirements: room.requirementCount,
              members: room.memberCount,
              guests: room.guestInviteCount,
              purgeAt: schedulePurgePreview(),
            },
          });
          if (!confirmed) return;
          const reason = await showInputDialog({
            titleKey: 'sedr.deletion.reasonTitle',
            labelKey: 'sedr.deletion.reasonPrompt',
            confirmLabelKey: 'sedr.deletion.request',
            multiline: true,
            required: true,
            maxLength: 2000,
          });
          if (!reason) return;
          try {
            await sedrApi.scheduleDeletion(roomId, reason);
            await onRoomChanged();
          } catch (cause) {
            showError(t(sedrErrorMessageKey(cause)));
          }
        })();
      });
    } else if (room.lifecycleState === 'DeletionRequested') {
      if (room.deletionRequestedBy === me) {
        appendBtn('sedr.deletion.withdraw', 'button subtle', () => {
          void (async () => {
            try {
              await sedrApi.cancelDeletion(roomId);
              await onRoomChanged();
            } catch (cause) {
              showError(t(sedrErrorMessageKey(cause)));
            }
          })();
        });
      } else {
        appendBtn('sedr.deletion.approve', 'button danger', () => {
          void (async () => {
            const confirmed = await showConfirmDialog({
              titleKey: 'sedr.deletion.approveTitle',
              messageKey: 'sedr.deletion.confirmApproveDetail',
              messageParams: {
                title: room.title,
                requester: room.deletionRequestedByName ?? '',
                reason: room.deletionReason?.trim() || '—',
                purgeAt: schedulePurgePreview(),
              },
            });
            if (!confirmed) return;
            try {
              await sedrApi.decideDeletion(roomId, true);
              await onRoomChanged();
            } catch (cause) {
              showError(t(sedrErrorMessageKey(cause)));
            }
          })();
        });
        appendBtn('sedr.deletion.reject', 'button subtle', () => {
          void (async () => {
            try {
              await sedrApi.decideDeletion(roomId, false);
              await onRoomChanged();
            } catch (cause) {
              showError(t(sedrErrorMessageKey(cause)));
            }
          })();
        });
      }
    } else if (room.lifecycleState === 'PendingDeletion') {
      appendBtn('sedr.deletion.cancel', 'button primary', () => {
        void (async () => {
          try {
            await sedrApi.cancelDeletion(roomId);
            await onRoomChanged();
          } catch (cause) {
            showError(t(sedrErrorMessageKey(cause)));
          }
        })();
      });
    }

    applyI18n(wrap);
  };

  identityForm.addEventListener('submit', (event) => {
    event.preventDefault();
    if (busy || !canEditSettings) return;
    void (async () => {
      busy = true;
      clearMessages();
      try {
        await sedrApi.patchRoom(roomId, {
          title: titleInput.value.trim(),
          clientOrganization: orgInput.value.trim(),
        });
        showInfo(t('sedr.settings.identitySaved'));
        await onRoomChanged();
      } catch (cause) {
        showError(t(sedrErrorMessageKey(cause)));
      } finally {
        busy = false;
      }
    })();
  });

  panel.querySelector('#sedr-settings-require-guest-totp')?.addEventListener('change', (event) => {
    const checkbox = event.target as HTMLInputElement;
    void (async () => {
      if (busy || !canEditSettings) return;
      const next = checkbox.checked;
      if (next === requireGuestTotp) return;
      if (next) {
        const confirmed = await showConfirmDialog({
          titleKey: 'sedr.access.requireGuestTotpEnableTitle',
          message: t('sedr.access.requireGuestTotpEnableConfirm'),
          confirmLabelKey: 'sedr.access.requireGuestTotpEnable',
        });
        if (!confirmed) {
          checkbox.checked = requireGuestTotp;
          return;
        }
      }
      busy = true;
      clearMessages();
      try {
        const updated = await sedrApi.patchRoom(roomId, { requireGuestTotp: next });
        requireGuestTotp = Boolean(updated.requireGuestTotp);
        checkbox.checked = requireGuestTotp;
        showInfo(t(requireGuestTotp
          ? 'sedr.access.requireGuestTotpEnabled'
          : 'sedr.access.requireGuestTotpDisabled'));
        await onRoomChanged();
      } catch (cause) {
        checkbox.checked = requireGuestTotp;
        showError(t(sedrErrorMessageKey(cause)));
      } finally {
        busy = false;
      }
    })();
  });

  panel.querySelector('#sedr-settings-allow-guest-links')?.addEventListener('change', (event) => {
    const checkbox = event.currentTarget as HTMLInputElement;
    void (async () => {
      if (busy || guestLinksForced || !canEditSettings) return;
      const next = checkbox.checked;
      if (next === allowGuestLinks) return;
      busy = true;
      clearMessages();
      try {
        const updated = await sedrApi.patchRoom(roomId, { allowGuestLinks: next });
        allowGuestLinks = Boolean(updated.allowGuestLinks);
        checkbox.checked = allowGuestLinks;
        showInfo(t(allowGuestLinks
          ? 'sedr.access.allowGuestLinksEnabled'
          : 'sedr.access.allowGuestLinksDisabled'));
        await onRoomChanged();
      } catch (cause) {
        checkbox.checked = allowGuestLinks;
        showError(t(sedrErrorMessageKey(cause)));
      } finally {
        busy = false;
      }
    })();
  });

  panel.querySelector('#sedr-settings-create-category')?.addEventListener('click', () => {
    showSedrRoomCategoryDialog({
      roomId,
      mode: 'create',
      onDone: async () => {
        categories = await sedrApi.listCategories(roomId);
        renderCategories();
      },
    });
  });

  panel.querySelector('#sedr-settings-add-link-type')?.addEventListener('click', () => {
    void (async () => {
      const result = await showSedrEditLinkTypeDialog({ mode: 'create' });
      if (!result) return;
      try {
        const created = await sedrApi.createRequirementLinkType(roomId, {
          name: result.name,
          inverseName: result.inverseName,
        });
        linkTypes = [...linkTypes, created];
        renderLinkTypes();
      } catch (cause) {
        showError(t(sedrErrorMessageKey(cause)));
      }
    })();
  });

  renderCategories();
  renderLinkTypes();
  renderLifecycle();
}
